import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useGetAvailableSoftwareVersions,
    useSetDeviceSoftwareVersion,
    useUpdateDevice,
} from '@/api/routes/devices'
import { useGetAllBusinesses } from '@/api/routes/businesses'
import { useGetAllModels } from '@/api/routes/models'
import { useQueryClient } from '@tanstack/react-query'
import type { Device } from './columns'

type BulkAction = 'softwareVersion' | 'business' | 'model' | 'cloudActive' | 'cloudActiveOverride'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    devices: Device[]
    action: BulkAction
}

export function BulkUpdateModal({ open, onOpenChange, devices, action }: Props) {
    const [selectedValue, setSelectedValue] = useState<string>('')
    const [updating, setUpdating] = useState(false)
    const [progress, setProgress] = useState(0)
    const [error, setError] = useState('')

    const queryClient = useQueryClient()
    const setVersion = useSetDeviceSoftwareVersion()
    const updateDevice = useUpdateDevice()

    const firstDeviceId = devices[0]?.id
    const { data: versions } = useGetAvailableSoftwareVersions(firstDeviceId)
    const { data: businesses } = useGetAllBusinesses()
    const { data: models } = useGetAllModels()

    const allSameModel = new Set(devices.map((d) => d.model?.name)).size === 1

    const getTitle = () => {
        switch (action) {
            case 'softwareVersion': return 'Set Software Version'
            case 'business': return 'Set Business'
            case 'model': return 'Set Model'
            case 'cloudActive': return 'Set Cloud Active'
            case 'cloudActiveOverride': return 'Set Cloud Override'
        }
    }

    const handleConfirm = async () => {
        if (!selectedValue) return
        setUpdating(true)
        setProgress(0)
        setError('')

        let completed = 0
        const errors: string[] = []

        for (const device of devices) {
            try {
                if (action === 'softwareVersion') {
                    await setVersion.mutateAsync({
                        deviceId: device.id,
                        versionId: Number(selectedValue),
                    })
                } else if (action === 'business') {
                    await updateDevice.mutateAsync({
                        id: device.id,
                        data: { business: selectedValue },
                    })
                } else if (action === 'model') {
                    await updateDevice.mutateAsync({
                        id: device.id,
                        data: { model: selectedValue },
                    })
                } else if (action === 'cloudActive') {
                    await updateDevice.mutateAsync({
                        id: device.id,
                        data: { cloudActive: selectedValue === 'true' },
                    })
                } else if (action === 'cloudActiveOverride') {
                    await updateDevice.mutateAsync({
                        id: device.id,
                        data: { cloudActiveOverride: selectedValue === 'true' },
                    })
                }
            } catch (err: any) {
                errors.push(`${device.name}: ${err.message || 'Failed'}`)
            }
            completed++
            setProgress(Math.round((completed / devices.length) * 100))
        }

        if (errors.length > 0) {
            setError(`${errors.length} device(s) failed to update`)
        }

        queryClient.invalidateQueries({ queryKey: ['devices'] })
        setUpdating(false)

        if (errors.length === 0) {
            handleClose(false)
        }
    }

    const handleClose = (open: boolean) => {
        if (updating) return
        onOpenChange(open)
        setSelectedValue('')
        setError('')
        setProgress(0)
    }

    const renderOptions = () => {
        if (action === 'softwareVersion') {
            if (!allSameModel) return <div className="text-sm text-muted-foreground">Select devices of the same model to set software version.</div>
            if (!versions || versions.length === 0) return <div className="text-sm text-muted-foreground">No software versions available for this model.</div>
            return (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                    {versions.map((v: any) => (
                        <label
                            key={v.id}
                            className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                selectedValue === String(v.id)
                                    ? 'border-foreground/30 bg-accent'
                                    : 'border-border hover:bg-accent/50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="option"
                                checked={selectedValue === String(v.id)}
                                onChange={() => setSelectedValue(String(v.id))}
                                className="accent-foreground"
                            />
                            <div>
                                <div className="text-sm font-medium">{v.name}</div>
                                {v.releaseDate && (
                                    <div className="text-xs text-muted-foreground">
                                        {new Date(v.releaseDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        </label>
                    ))}
                </div>
            )
        }

        if (action === 'business') {
            return (
                <Select value={selectedValue} onValueChange={setSelectedValue}>
                    <SelectTrigger>
                        <SelectValue placeholder="Select business..." />
                    </SelectTrigger>
                    <SelectContent>
                        {(businesses || []).map((b: any) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )
        }

        if (action === 'model') {
            return (
                <Select value={selectedValue} onValueChange={setSelectedValue}>
                    <SelectTrigger>
                        <SelectValue placeholder="Select model..." />
                    </SelectTrigger>
                    <SelectContent>
                        {(models || []).map((m: any) => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )
        }

        if (action === 'cloudActive' || action === 'cloudActiveOverride') {
            const label = action === 'cloudActive' ? 'cloud active' : 'cloud override'
            return (
                <Select value={selectedValue} onValueChange={setSelectedValue}>
                    <SelectTrigger>
                        <SelectValue placeholder={`Set ${label}...`} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="true">Enable</SelectItem>
                        <SelectItem value="false">Disable</SelectItem>
                    </SelectContent>
                </Select>
            )
        }
    }

    const canConfirm = selectedValue && !updating && (action !== 'softwareVersion' || allSameModel)

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{getTitle()}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="text-sm text-muted-foreground">
                        Update <span className="font-medium text-foreground">{devices.length}</span> device{devices.length !== 1 ? 's' : ''}
                    </div>

                    {renderOptions()}

                    {updating && (
                        <div className="space-y-1">
                            <div className="h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full bg-foreground transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="text-xs text-muted-foreground text-right">{progress}%</div>
                        </div>
                    )}

                    {error && <div className="text-sm text-red-600">{error}</div>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleClose(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={!canConfirm}>
                        {updating ? 'Updating...' : 'Update Devices'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
